import React, { ReactElement } from "react";
//#region types
import { FormControlLayoutProps, FormRowProps } from "models/propTypes/genericFormProps";
import { FormEnums } from "models/enums";
import { FormGridLayout } from "models/propTypes/basePropTypes";
import { MessagePlacement } from "models/propTypes/messagePropTypes";
//#endregion
//#region components
import FormFieldGroup from "./formFields/formFieldGroup";
import FormFieldWrapper from "./formFieldWrapper";
import FormControlLayout from "./rowLayouts/formControlLayout";
import { GridColumnCount } from "models/utilTypes/uiTypes";
import { useFormikContext } from "formik";
import { FormHelper } from "utils/helpers";
//#endregion

function VerticalFormControl<T extends Record<string, any>>(props: FormRowProps<T>) {
  const { name, field: fieldDefinition } = props;
  const { values } = useFormikContext<T>();

  const field = { ...fieldDefinition };

  const formGridLayout = React.useMemo(() => {
    const formGridLayout: FormGridLayout = props.formGridLayout
      ? { ...props.formGridLayout }
      : {
          message: props.enableControlLables ? 4 : undefined,
        };

    if (
      field.type === FormEnums.FormFieldType.TABLELIST ||
      field.type === FormEnums.FormFieldType.EDITABLETABLE ||
      field.type === FormEnums.FormFieldType.SUGGESTIONS
    ) {
      formGridLayout.message = false;
    } else if (field.type === FormEnums.FormFieldType.TOGGLE) {
      formGridLayout.label = true;
      formGridLayout.message =
        props.enableControlLables || props.formGridLayout?.message ? formGridLayout.message : false;
    } else if (!props.enableControlLables && typeof field.widthColumnCount === "number") {
      formGridLayout.message = (12 - field.widthColumnCount) as GridColumnCount;
      field.widthColumnCount = undefined;
    }

    return formGridLayout;
  }, [field]);

  const getFormField = (layout?: ReactElement<FormControlLayoutProps>) => {
    const messagePlacement: MessagePlacement = typeof formGridLayout.message === "number" ? "right" : "top";

    const hideOptionalIndicator = FormHelper.evaluateHideOptionalIndicator(field.hideOptionalIndicator, values);

    return field.type === FormEnums.FormFieldType.HORIONTALFIELDGROUP ? (
      <FormFieldGroup<T> {...props} field={field} messagePlacement={messagePlacement} layout={layout} />
    ) : (
      <FormFieldWrapper<T>
        {...props}
        field={field}
        messagePlacement={messagePlacement}
        layout={layout}
        hideOptionalIndicator={hideOptionalIndicator}
      />
    );
  };

  const formControlLayoutProps: FormControlLayoutProps = {
    names: [name],
    label: field.label,
    controlLabel: field.controlLabel,
    descriptionText: field.descriptionText,
    inline: field.type === FormEnums.FormFieldType.TOGGLE,
    verticalAlignLabelCenter: field.type === FormEnums.FormFieldType.TOGGLE,
    enableControlLables: field.type === FormEnums.FormFieldType.CHOICE || props.enableControlLables,
    contentWidth: props.contentWidth,
    formGridLayout: formGridLayout,
    className: field.classes?.formControl,
  };

  return field.type === FormEnums.FormFieldType.DIMENSIONSELECTOR ||
    field.type === FormEnums.FormFieldType.DIMENSIONMULTIPLEINPUTSELECTOR ||
    field.type === FormEnums.FormFieldType.DIMENSIONINPUTSELECTOR ||
    field.type === FormEnums.FormFieldType.HORIONTALFIELDGROUP ||
    field.type === FormEnums.FormFieldType.SUBFORM ? (
    getFormField(<FormControlLayout {...formControlLayoutProps}></FormControlLayout>)
  ) : (
    <FormControlLayout {...formControlLayoutProps}>{getFormField()}</FormControlLayout>
  );
}

export default VerticalFormControl;
